import React, { useState, useEffect } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { Pagination } from '../../../components/Pagination';
import { accessService } from '../../../services/central-api/access';
import { ClientError } from '../../../services/central-api/base-service';
import { AccessParamList } from '../../../typings/Params';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import MyDataTable from '../../../components/MyDataTable';
import FilterInput from '../../../components/Filter/components/FilterInput';
import CustomButton from '../../../components/CustomButton';
import Filter from './Filters';
import useFilters from '../../../components/Filter/hooks/useFilters';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import ActingBodyCommandModal from './ActingBodyCommandModal';

export default function ActingBodyCommands({ navigation }: { navigation: Navigation; }) {
    const route = useRoute<RouteProp<AccessParamList, 'ActingBodyCommands'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<AccessParamList['ActingBodyCommands']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [selectedCommand, setSelectedCommand] = useState<ActingBodyCommand>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [commands, setCommands] = useState<ActingBodyCommand[]>([]);
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [totalItems, setTotalItems] = useState(0);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessActingBodyCommands', filters],
        queryFn: () => accessService.getPaginatedActingBodyCommands(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setCommands(data.rows);
        setTotalItems(data.count);
    }, [data]);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));

            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
        }
    }

    async function confirmDelete(command: ActingBodyCommand) {
        try {
            await accessService.deleteActingBodyCommand(command.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            setDeleteModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return <Container>
        <Header
            title={translate('commands')}
            actions={<CustomButton
                text={translate('register')}
                onPress={() => {
                    setSelectedCommand(undefined);
                    setIsModalVisible(true);
                }}
            />}
        />
        <Card>
            <DataTableHeader
                zIndex={2}
                title={translate('registeredCommands')}
                filter={<Filter
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter} />}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={text => {
                        setTextFilter(text);
                    }}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('name'),
                        selector: row => row.name,
                        wrap: true,
                        grow: 3
                    },
                    {
                        name: translate('actingBody'),
                        selector: row => row.actingBody?.name ?? '',
                        wrap: true,
                        grow: 1,
                    },
                    {
                        button: true,
                        cell: row => <DropdownButton
                            actions={[{
                                content: translate('editInformation'),
                                onPress: () => {
                                    setSelectedCommand(row);
                                    setIsModalVisible(true);
                                }
                            }, {
                                content: translate('delete'),
                                variant: 'danger',
                                onPress: () => {
                                    (row); setSelectedCommand(row);
                                    setDeleteModalVisible(true);
                                },
                            }]}
                        />
                    }]}
                    data={commands}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    totalItems={totalItems}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>
        {selectedCommand &&
            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={selectedCommand.name}
                onSubmit={async () => {
                    await confirmDelete(selectedCommand);
                }}
            />
        }

        <ActingBodyCommandModal
            setModalVisible={setIsModalVisible}
            isModalVisible={isModalVisible}
            actingBodyCommandId={selectedCommand?.id}
            onClose={refetch}
        />
    </Container>;
}
